<template>
  <div class="prod-page" :class="{ dark: isDarkMode }">

    <ModalLoading :isLoading="isLoading" />

    <!-- HEADER -->
    <div class="prod-header">
      <div class="prod-header-left">
        <h2><i class="bi bi-gear-wide-connected"></i> Producción</h2>
        <p class="prod-subtitle">Gestiona recetas y órdenes de producción</p>
      </div>
      <div class="prod-header-btns">
        <button v-if="activeTab === 'recetas'" class="btn-gold" @click="abrirModalReceta(null)">
          <i class="bi bi-plus"></i> Nueva Receta
        </button>
        <button v-if="activeTab === 'ordenes'" class="btn-gold" @click="abrirModalOrden">
          <i class="bi bi-plus"></i> Nueva Orden
        </button>
      </div>
    </div>

    <!-- TABS -->
    <div class="prod-tabs">
      <button class="prod-tab" :class="{ active: activeTab === 'recetas' }" @click="activeTab = 'recetas'">
        <i class="bi bi-book"></i> Recetas
      </button>
      <button class="prod-tab" :class="{ active: activeTab === 'ordenes' }" @click="activeTab = 'ordenes'">
        <i class="bi bi-clipboard2-check"></i> Órdenes de Producción
      </button>
    </div>

    <!-- ═══════════ TAB RECETAS ═══════════ -->
    <div v-if="activeTab === 'recetas'" class="prod-tab-body">
      <div v-if="!recetas.length" class="prod-empty">
        <i class="bi bi-book"></i>
        <p>No hay recetas registradas.</p>
        <p class="text-muted">Crea una receta para definir los ingredientes de un producto terminado.</p>
      </div>
      <div v-else class="table-wrap-outer">
        <div class="table-wrap">
        <table class="prod-table">
          <thead>
            <tr>
              <th>Receta</th>
              <th>Producto Terminado</th>
              <th class="tc">Unidades / Corrida</th>
              <th class="tc">Ingredientes</th>
              <th class="tc">Estado</th>
              <th class="tc">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in recetasPaginadas" :key="r.id_receta" :class="{ 'row-inactive': !r.activo }">
              <td class="cell-name">{{ r.nombre }}</td>
              <td>{{ r.producto?.nombre || '—' }}</td>
              <td class="tc">{{ r.unidades_por_corrida }}</td>
              <td class="tc">{{ r.ingredientes?.length || 0 }}</td>
              <td class="tc">
                <span class="badge" :class="r.activo ? 'badge-active' : 'badge-off'">
                  {{ r.activo ? 'Activa' : 'Inactiva' }}
                </span>
              </td>
              <td class="tc actions-cell">
                <button class="btn-icon icon-amber" title="Editar receta" @click="abrirModalReceta(r)">
                  <i class="bi bi-pencil-fill"></i>
                </button>
                <button class="btn-icon" :title="r.activo ? 'Desactivar' : 'Activar'" @click="cambiarEstadoReceta(r)">
                  <i class="bi" :class="r.activo ? 'bi-toggle-on icon-green' : 'bi-toggle-off icon-muted'"></i>
                </button>
                <button class="btn-icon icon-red" title="Eliminar receta" @click="confirmarEliminarReceta(r)">
                  <i class="bi bi-trash-fill"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
        <div class="pagination-wrapper">
          <div class="pagination-info">
            Mostrando {{ recetas.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }} a
            {{ Math.min(currentPage * pageSize, recetas.length) }}
            de {{ recetas.length }} registros
          </div>
          <div class="pagination-container">
            <button class="pagination-button" :disabled="currentPage === 1" @click="previousPage">Anterior</button>
            <button class="pagination-button" :disabled="currentPage === totalPages" @click="nextPage">Siguiente</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════ TAB ÓRDENES ═══════════ -->
    <div v-if="activeTab === 'ordenes'" class="prod-tab-body">
      <div class="prod-filters">
        <div class="filter-item">
          <label>Estado</label>
          <select v-model="filtroOrden" class="prod-select" @change="cargarOrdenes">
            <option value="">Todos</option>
            <option value="borrador">Borrador</option>
            <option value="completada">Completada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
        <button class="btn-icon-sm" title="Actualizar" @click="cargarOrdenes">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      <div v-if="!ordenes.length" class="prod-empty">
        <i class="bi bi-clipboard2-check"></i>
        <p>No hay órdenes de producción.</p>
      </div>
      <div v-else class="table-wrap-outer">
        <div class="table-wrap">
        <table class="prod-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Receta</th>
              <th>Producto</th>
              <th class="tc">Cantidad</th>
              <th class="tr">Costo Total</th>
              <th class="tr">Costo/Unidad</th>
              <th class="tr">P. Venta</th>
              <th class="tr">Margen Real</th>
              <th class="tc">Estado</th>
              <th class="tc">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in ordenesPaginadas" :key="o.id_orden">
              <td>{{ fmtFecha(o.created_at) }}</td>
              <td>{{ o.receta?.nombre || '—' }}</td>
              <td>{{ o.receta?.producto?.nombre || '—' }}</td>
              <td class="tc">{{ o.cantidad_a_producir }}</td>
              <td class="tr mono">L. {{ fmtNum(o.costo_total) }}</td>
              <td class="tr mono">L. {{ fmtNum(o.costo_por_unidad) }}</td>
              <td class="tr mono">L. {{ fmtNum(o.receta?.producto?.precio_unitario) }}</td>
              <td class="tr mono">
                <span :class="margenClass(o)">{{ fmtMargen(o) }}</span>
              </td>
              <td class="tc">
                <span class="badge" :class="badgeOrden(o.estado)">{{ o.estado }}</span>
              </td>
              <td class="tc actions-cell">
                <button class="btn-icon icon-teal" title="Ver detalle" @click="verDetalleOrden(o.id_orden)">
                  <i class="bi bi-eye-fill"></i>
                </button>
                <button v-if="o.estado === 'borrador'" class="btn-icon icon-green" title="Ejecutar orden"
                  @click="confirmarEjecutar(o)">
                  <i class="bi bi-play-circle-fill"></i>
                </button>
                <button v-if="o.estado === 'borrador'" class="btn-icon icon-red" title="Cancelar orden"
                  @click="confirmarCancelar(o)">
                  <i class="bi bi-x-circle-fill"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
        <div class="pagination-wrapper">
          <div class="pagination-info">
            Mostrando {{ ordenes.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }} a
            {{ Math.min(currentPage * pageSize, ordenes.length) }}
            de {{ ordenes.length }} registros
          </div>
          <div class="pagination-container">
            <button class="pagination-button" :disabled="currentPage === 1" @click="previousPage">Anterior</button>
            <button class="pagination-button" :disabled="currentPage === totalPages" @click="nextPage">Siguiente</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════
         MODAL: CREAR / EDITAR RECETA
    ════════════════════════════════════════════════════════ -->
    <div v-if="modalReceta.visible" class="modal-overlay">
      <div class="modal-box modal-xl">
        <button class="modal-close-x" @click="cerrarModalReceta"><i class="bi bi-x-lg"></i></button>
        <div class="modal-header">
          <h3><i class="bi bi-book"></i> {{ modalReceta.editando ? 'Editar Receta' : 'Nueva Receta' }}</h3>
        </div>
        <div class="modal-body">
          <div class="form-grid-3">
            <div class="form-group span-2">
              <label>Nombre de la Receta <span class="req">*</span></label>
              <input v-model="recetaForm.nombre" class="prod-input" placeholder="Ej: Tajadas de Plátano Estándar" />
            </div>
            <div class="form-group">
              <label>Unidades por Corrida <span class="req">*</span></label>
              <input v-model.number="recetaForm.unidades_por_corrida" type="number" min="1" class="prod-input" placeholder="50" />
            </div>
            <div class="form-group span-3">
              <label>Producto Terminado <span class="req">*</span></label>
              <select v-model="recetaForm.id_producto_terminado" class="prod-select">
                <option value="">Seleccionar producto terminado...</option>
                <option v-for="p in productosTerminados" :key="p.id_producto" :value="p.id_producto">
                  {{ p.nombre }}
                </option>
              </select>
              <p v-if="!productosTerminados.length" class="field-note">
                <i class="bi bi-info-circle"></i> No hay productos de tipo "Terminado". Configura el tipo en la sección de Productos.
              </p>
            </div>
          </div>

          <!-- INGREDIENTES -->
          <div class="ing-section">
            <div class="ing-header">
              <span class="ing-title"><i class="bi bi-list-ul"></i> Ingredientes (Materias Primas)</span>
              <button class="btn-secondary btn-sm" @click="agregarIngrediente">
                <i class="bi bi-plus"></i> Agregar ingrediente
              </button>
            </div>
            <div v-if="!recetaForm.ingredientes.length" class="ing-empty">
              <i class="bi bi-exclamation-circle"></i> Agrega al menos un ingrediente a la receta.
            </div>
            <div v-else class="ing-list">
              <div v-for="(ing, idx) in recetaForm.ingredientes" :key="idx" class="ing-row">
                <div class="ing-num">{{ idx + 1 }}</div>
                <select v-model="ing.id_materia_prima" class="prod-select ing-select">
                  <option value="">Seleccionar materia prima...</option>
                  <option v-for="mp in materiasPrimas" :key="mp.id_producto" :value="mp.id_producto">
                    {{ mp.nombre }}
                  </option>
                </select>
                <div class="ing-cant-wrap">
                  <input v-model.number="ing.paquetes" type="number" min="1" step="1"
                    class="prod-input ing-paq" placeholder="Paq." title="Paquetes" />
                  <span class="ing-sep">×</span>
                  <input v-model.number="ing.cantidad" type="number" min="0.001" step="0.001"
                    class="prod-input ing-cant" placeholder="Cant." />
                  <span class="ing-unidad">{{ getUnidadIng(ing.id_materia_prima) }}</span>
                </div>
                <span class="ing-total" v-if="ing.paquetes > 1 || ing.cantidad > 0">
                  = {{ ((ing.paquetes || 1) * (ing.cantidad || 0)).toFixed(3).replace(/\.?0+$/, '') }}
                </span>
                <button class="btn-icon icon-red" title="Quitar" @click="quitarIngrediente(idx)">
                  <i class="bi bi-trash-fill"></i>
                </button>
              </div>
            </div>
            <p v-if="!materiasPrimas.length" class="field-note">
              <i class="bi bi-info-circle"></i> No hay productos de tipo "Materia Prima". Configúralos en la sección de Productos.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarModalReceta">Cancelar</button>
          <button class="btn-primary" :disabled="savingReceta" @click="guardarReceta">
            <i class="bi" :class="savingReceta ? 'bi-arrow-repeat spin' : 'bi-check-lg'"></i>
            {{ savingReceta ? 'Guardando...' : (modalReceta.editando ? 'Actualizar Receta' : 'Crear Receta') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════
         MODAL: NUEVA ORDEN
    ════════════════════════════════════════════════════════ -->
    <div v-if="modalOrden.visible" class="modal-overlay">
      <div class="modal-box modal-md">
        <button class="modal-close-x" @click="cerrarModalOrden"><i class="bi bi-x-lg"></i></button>
        <div class="modal-header">
          <h3><i class="bi bi-clipboard2-plus"></i> Nueva Orden de Producción</h3>
        </div>
        <div class="modal-body">

          <!-- Si ya se creó, mostrar solo preview -->
          <div v-if="ordenPreview" class="order-preview">
            <div class="preview-banner">
              <i class="bi bi-check-circle-fill"></i>
              Orden <strong>#{{ ordenPreview.id_orden }}</strong> creada en estado <strong>Borrador</strong>
            </div>
            <div class="preview-costs">
              <div class="preview-cost-item">
                <span>Costo Total</span>
                <strong>L. {{ fmtNum(ordenPreview.costo_total) }}</strong>
              </div>
              <div class="preview-cost-item">
                <span>Costo por Unidad</span>
                <strong>L. {{ fmtNum(ordenPreview.costo_por_unidad) }}</strong>
              </div>
              <div class="preview-cost-item highlight">
                <span>Precio Sugerido</span>
                <strong>L. {{ fmtNum(ordenPreview.precio_sugerido) }}</strong>
              </div>
            </div>
            <div v-if="ordenPreview.stock_info?.length" class="stock-check">
              <div class="stock-check-title">Disponibilidad de Materias Primas:</div>
              <div v-for="s in ordenPreview.stock_info" :key="s.nombre" class="stock-row">
                <span class="stock-nombre">{{ s.nombre }}</span>
                <span class="stock-vals" :class="s.alcanza ? 'ok' : 'warn'">
                  {{ fmtNum(s.stock_disponible) }} disp. / {{ fmtNum(s.cantidad_requerida) }} req.
                  <i class="bi" :class="s.alcanza ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
                </span>
              </div>
              <p v-if="ordenPreview.stock_info.some(s => !s.alcanza)" class="stock-warn-note">
                <i class="bi bi-exclamation-triangle-fill"></i>
                Stock insuficiente en algunos ingredientes. Puedes ejecutar la orden cuando repares el inventario.
              </p>
            </div>
            <p class="preview-hint">Ve a la lista de órdenes para ejecutarla cuando el inventario esté listo.</p>
          </div>

          <!-- Formulario de creación -->
          <template v-else>
            <div class="form-group">
              <label>Receta <span class="req">*</span></label>
              <select v-model="ordenForm.id_receta" class="prod-select">
                <option value="">Seleccionar receta activa...</option>
                <option v-for="r in recetasActivas" :key="r.id_receta" :value="r.id_receta">
                  {{ r.nombre }} → {{ r.producto?.nombre }}
                </option>
              </select>
            </div>
            <div v-if="precioVentaActual !== null" class="precio-venta-info">
              <i class="bi bi-tag-fill"></i>
              Precio de venta actual del producto:
              <strong>L. {{ fmtNum(precioVentaActual) }}</strong>
            </div>

            <div class="form-grid-2">
              <div class="form-group">
                <label>Cantidad a Producir <span class="req">*</span></label>
                <input v-model.number="ordenForm.cantidad_a_producir" type="number" min="1"
                  class="prod-input" placeholder="Unidades" />
              </div>
              <div class="form-group">
                <label>Margen de Ganancia (%)</label>
                <input v-model.number="ordenForm.margen_ganancia" type="number" min="0"
                  class="prod-input" placeholder="40" />
              </div>
            </div>
            <div class="form-group">
              <label>Notas (opcional)</label>
              <textarea v-model="ordenForm.notas" class="prod-input prod-textarea"
                rows="2" placeholder="Observaciones sobre esta orden..."></textarea>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarModalOrden">
            {{ ordenPreview ? 'Cerrar' : 'Cancelar' }}
          </button>
          <button v-if="!ordenPreview" class="btn-primary" :disabled="savingOrden" @click="crearOrdenSubmit">
            <i class="bi" :class="savingOrden ? 'bi-arrow-repeat spin' : 'bi-check-lg'"></i>
            {{ savingOrden ? 'Procesando...' : 'Crear Orden' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════
         MODAL: DETALLE DE ORDEN
    ════════════════════════════════════════════════════════ -->
    <div v-if="modalDetalle.visible" class="modal-overlay">
      <div class="modal-box modal-xl">
        <button class="modal-close-x" @click="cerrarDetalle"><i class="bi bi-x-lg"></i></button>
        <div class="modal-header">
          <h3><i class="bi bi-clipboard2-check"></i> Detalle de Orden</h3>
        </div>
        <div class="modal-body">
          <template v-if="modalDetalle.data">
            <div class="detalle-info-grid">
              <div class="detalle-info-item"><span>Receta</span><strong>{{ modalDetalle.data.receta?.nombre }}</strong></div>
              <div class="detalle-info-item"><span>Producto</span><strong>{{ modalDetalle.data.receta?.producto?.nombre }}</strong></div>
              <div class="detalle-info-item"><span>Cantidad producida</span><strong>{{ modalDetalle.data.cantidad_a_producir }} und.</strong></div>
              <div class="detalle-info-item"><span>Estado</span>
                <span class="badge" :class="badgeOrden(modalDetalle.data.estado)">{{ modalDetalle.data.estado }}</span>
              </div>
              <div class="detalle-info-item"><span>Costo Total</span><strong class="mono">L. {{ fmtNum(modalDetalle.data.costo_total) }}</strong></div>
              <div class="detalle-info-item"><span>Costo por Unidad</span><strong class="mono">L. {{ fmtNum(modalDetalle.data.costo_por_unidad) }}</strong></div>
              <div class="detalle-info-item"><span>Precio de Venta</span><strong class="mono">L. {{ fmtNum(modalDetalle.data.receta?.producto?.precio_unitario) }}</strong></div>
              <div class="detalle-info-item"><span>Margen Real</span>
                <strong class="mono" :class="margenClass(modalDetalle.data)">{{ fmtMargen(modalDetalle.data) }}</strong>
              </div>
              <div class="detalle-info-item"><span>Sucursal</span><strong>{{ modalDetalle.data.sucursal?.nombre_administrativo || '—' }}</strong></div>
              <div class="detalle-info-item"><span>Fecha</span><strong>{{ fmtFecha(modalDetalle.data.created_at) }}</strong></div>
              <div v-if="modalDetalle.data.notas" class="detalle-info-item span-full"><span>Notas</span><strong>{{ modalDetalle.data.notas }}</strong></div>
            </div>

            <template v-if="modalDetalle.data.detalles?.length">
              <div class="detalle-section-title"><i class="bi bi-list-check"></i> Materias Primas Consumidas</div>
              <table class="prod-table">
                <thead>
                  <tr>
                    <th>Materia Prima</th>
                    <th class="tc">Cantidad Usada</th>
                    <th class="tr">Costo Unit. (momento)</th>
                    <th class="tr">Costo Línea</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in modalDetalle.data.detalles" :key="d.id_detalle">
                    <td>{{ d.materia_prima?.nombre }}</td>
                    <td class="tc mono">{{ fmtNum(d.cantidad_requerida) }} {{ d.materia_prima?.unidad_de_medida?.medida || 'u' }}</td>
                    <td class="tr mono">L. {{ fmtNum(d.costo_unitario_momento) }}</td>
                    <td class="tr mono">L. {{ fmtNum(d.costo_total_linea) }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
            <div v-else-if="modalDetalle.data.estado === 'borrador'" class="prod-empty small">
              <i class="bi bi-info-circle"></i>
              <p>Orden en borrador. Los detalles de consumo aparecerán al ejecutar.</p>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════
         MODAL: CONFIRMAR EJECUTAR
    ════════════════════════════════════════════════════════ -->
    <div v-if="modalEjecutar.visible" class="modal-overlay">
      <div class="modal-box modal-sm">
        <button class="modal-close-x" @click="modalEjecutar.visible = false"><i class="bi bi-x-lg"></i></button>
        <div class="modal-header">
          <h3><i class="bi bi-play-circle"></i> Ejecutar Orden</h3>
        </div>
        <div class="modal-body">
          <p>¿Confirmas ejecutar la orden <strong>#{{ modalEjecutar.orden?.id_orden }}</strong>?</p>
          <p class="text-muted">Se consumirán las materias primas del inventario y se agregarán
            <strong>{{ modalEjecutar.orden?.cantidad_a_producir }}</strong> unidades de
            <strong>{{ modalEjecutar.orden?.receta?.producto?.nombre }}</strong>.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="modalEjecutar.visible = false">Cancelar</button>
          <button class="btn-primary" :disabled="ejecutando" @click="doEjecutarOrden">
            <i class="bi" :class="ejecutando ? 'bi-arrow-repeat spin' : 'bi-play-fill'"></i>
            {{ ejecutando ? 'Ejecutando...' : 'Sí, Ejecutar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════
         MODAL: CONFIRMAR CANCELAR
    ════════════════════════════════════════════════════════ -->
    <div v-if="modalCancelar.visible" class="modal-overlay">
      <div class="modal-box modal-sm">
        <button class="modal-close-x" @click="modalCancelar.visible = false"><i class="bi bi-x-lg"></i></button>
        <div class="modal-header">
          <h3><i class="bi bi-x-circle"></i> Cancelar Orden</h3>
        </div>
        <div class="modal-body">
          <p>¿Cancelar la orden <strong>#{{ modalCancelar.orden?.id_orden }}</strong>? Esta acción no se puede deshacer.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="modalCancelar.visible = false">No</button>
          <button class="btn-danger" :disabled="cancelando" @click="doCancelarOrden">
            <i class="bi" :class="cancelando ? 'bi-arrow-repeat spin' : 'bi-x-circle-fill'"></i>
            {{ cancelando ? 'Cancelando...' : 'Sí, Cancelar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════
         MODAL: CONFIRMAR ELIMINAR RECETA
    ════════════════════════════════════════════════════════ -->
    <div v-if="modalEliminarReceta.visible" class="modal-overlay">
      <div class="modal-box modal-sm">
        <button class="modal-close-x" @click="modalEliminarReceta.visible = false"><i class="bi bi-x-lg"></i></button>
        <div class="modal-header">
          <h3><i class="bi bi-trash"></i> Eliminar Receta</h3>
        </div>
        <div class="modal-body">
          <p>¿Eliminar la receta <strong>{{ modalEliminarReceta.receta?.nombre }}</strong>?</p>
          <p class="text-muted">Esta acción no se puede deshacer. Se eliminarán también todos sus ingredientes.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="modalEliminarReceta.visible = false">Cancelar</button>
          <button class="btn-danger" :disabled="eliminandoReceta" @click="doEliminarReceta">
            <i class="bi" :class="eliminandoReceta ? 'bi-arrow-repeat spin' : 'bi-trash-fill'"></i>
            {{ eliminandoReceta ? 'Eliminando...' : 'Sí, Eliminar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- TOAST -->
    <div v-if="toast.visible" class="prod-toast" :class="'prod-toast--' + toast.type">
      <i class="bi" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
      {{ toast.msg }}
    </div>

  </div>
</template>

<script>
import {
  getMateriasPrimas,
  getProductosTerminados,
  getRecetas,
  crearReceta      as apiCrearReceta,
  actualizarReceta as apiActualizarReceta,
  toggleReceta     as apiToggleReceta,
  eliminarReceta   as apiEliminarReceta,
  getOrdenes,
  getOrdenDetalle,
  crearOrden      as apiCrearOrden,
  ejecutarOrden   as apiEjecutarOrden,
  cancelarOrden   as apiCancelarOrden,
} from '../../services/produccionSolicitudes.js';
import { setPageTitle } from "@/components/pageMetadata";
import ModalLoading from "@/components/ModalLoading.vue";

export default {
  name: 'ProduccionPage',
  components: { ModalLoading },

  data() {
    return {
      isDarkMode:  false,
      isLoading:   false,
      activeTab:   'recetas',
      idUsuario:   null,

      // Catálogos
      materiasPrimas:      [],
      productosTerminados: [],

      // Recetas
      recetas:       [],
      loadingRecetas: false,

      // Modal receta
      modalReceta:      { visible: false, editando: false },
      editandoRecetaId: null,
      recetaForm: {
        nombre:                '',
        id_producto_terminado: '',
        unidades_por_corrida:  1,
        ingredientes:          [],
      },
      savingReceta: false,

      // Órdenes
      ordenes:       [],
      loadingOrdenes: false,
      filtroOrden:   '',

      // Modal nueva orden
      modalOrden:  { visible: false },
      ordenForm: {
        id_receta:           '',
        cantidad_a_producir: 1,
        margen_ganancia:     40,
        notas:               '',
      },
      ordenPreview:  null,
      savingOrden:   false,
      ordenCreada:   false,

      // Modal detalle
      modalDetalle:   { visible: false, data: null },
      loadingDetalle: false,

      // Modal eliminar receta
      modalEliminarReceta: { visible: false, receta: null },
      eliminandoReceta:    false,

      // Modal ejecutar / cancelar
      modalEjecutar: { visible: false, orden: null },
      ejecutando:    false,
      modalCancelar: { visible: false, orden: null },
      cancelando:    false,

      // Toast
      toast: { visible: false, msg: '', type: 'success' },

      // Paginación
      currentPage: 1,
      pageSize:    10,
    };
  },

  computed: {
    recetasActivas() {
      return this.recetas.filter(r => r.activo);
    },
    precioVentaActual() {
      if (!this.ordenForm.id_receta) return null;
      const receta = this.recetas.find(r => r.id_receta === Number(this.ordenForm.id_receta));
      if (!receta) return null;
      const idProducto = receta.producto?.id_producto || receta.id_producto_terminado;
      const producto = this.productosTerminados.find(p => p.id_producto === idProducto);
      return producto?.precio_unitario ?? null;
    },
    recetasPaginadas() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.recetas.slice(start, start + this.pageSize);
    },
    ordenesPaginadas() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.ordenes.slice(start, start + this.pageSize);
    },
    listaActual() {
      return this.activeTab === 'recetas' ? this.recetas : this.ordenes;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.listaActual.length / this.pageSize));
    },
  },

  watch: {
    activeTab() { this.currentPage = 1; },
    filtroOrden() { this.currentPage = 1; },
  },

  async mounted() {
    setPageTitle("Producción");
    this.isDarkMode = localStorage.getItem('isDarkMode') === 'true';
    this._darkListener = () => { this.isDarkMode = localStorage.getItem('isDarkMode') === 'true'; };
    window.addEventListener('poswave:toggle-dark', this._darkListener);
    window.addEventListener('keydown', this._escHandler = this.onEsc.bind(this));

    const token = localStorage.getItem('auth');
    this.idUsuario = token ? JSON.parse(atob(token.split('.')[1])).id_usuario : null;
    if (!this.idUsuario) { this.$router.push('/login'); return; }

    await Promise.all([
      this.cargarProductos(),
      this.cargarRecetas(),
      this.cargarOrdenes(),
    ]);
  },

  beforeUnmount() {
    window.removeEventListener('poswave:toggle-dark', this._darkListener);
    window.removeEventListener('keydown', this._escHandler);
  },

  methods: {
    previousPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage()     { if (this.currentPage < this.totalPages) this.currentPage++; },
    onEsc(e) {
      if (e.key !== 'Escape') return;
      if (this.modalReceta.visible)   { this.cerrarModalReceta(); return; }
      if (this.modalOrden.visible)    { this.cerrarModalOrden();  return; }
      if (this.modalDetalle.visible)  { this.cerrarDetalle();     return; }
      if (this.modalEliminarReceta.visible) { this.modalEliminarReceta.visible = false; return; }
      if (this.modalEjecutar.visible) { this.modalEjecutar.visible = false; return; }
      if (this.modalCancelar.visible) { this.modalCancelar.visible = false; }
    },

    // ─── Carga ─────────────────────────────────────────────────────────────

    async cargarProductos() {
      try {
        const [mps, term] = await Promise.all([
          getMateriasPrimas(this.idUsuario),
          getProductosTerminados(this.idUsuario),
        ]);
        this.materiasPrimas      = mps  || [];
        this.productosTerminados = term || [];
      } catch (e) {
        console.error('Error cargando catálogos de producción:', e);
      }
    },

    async cargarRecetas() {
      this.isLoading = true;
      try {
        this.recetas = await getRecetas(this.idUsuario) || [];
      } catch {
        this.showToast('Error al cargar recetas', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async cargarOrdenes() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filtroOrden) params.estado = this.filtroOrden;
        this.ordenes = await getOrdenes(this.idUsuario, params) || [];
      } catch {
        this.showToast('Error al cargar órdenes', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    // ─── Modal Receta ───────────────────────────────────────────────────────

    abrirModalReceta(receta) {
      this.recetaForm = {
        nombre:                receta?.nombre || '',
        id_producto_terminado: receta?.producto?.id_producto || '',
        unidades_por_corrida:  receta?.unidades_por_corrida || 1,
        ingredientes: (receta?.ingredientes || []).map(i => ({
          id_materia_prima: i.materia_prima?.id_producto || '',
          paquetes:         1,
          cantidad:         i.cantidad,
        })),
      };
      this.editandoRecetaId = receta?.id_receta || null;
      this.modalReceta      = { visible: true, editando: !!receta };
    },

    cerrarModalReceta() { this.modalReceta.visible = false; },

    agregarIngrediente() {
      this.recetaForm.ingredientes.push({ id_materia_prima: '', paquetes: 1, cantidad: 1 });
    },
    quitarIngrediente(idx) {
      this.recetaForm.ingredientes.splice(idx, 1);
    },
    getUnidadIng(id_materia_prima) {
      const mp = this.materiasPrimas.find(m => m.id_producto === id_materia_prima);
      return mp?.unidad_de_medida?.medida || '';
    },

    async guardarReceta() {
      const f = this.recetaForm;
      if (!f.nombre.trim())             { this.showToast('El nombre es requerido', 'error'); return; }
      if (!f.id_producto_terminado)     { this.showToast('Selecciona un producto terminado', 'error'); return; }
      if (!f.unidades_por_corrida || f.unidades_por_corrida < 1)
                                        { this.showToast('Unidades por corrida inválidas', 'error'); return; }
      if (!f.ingredientes.length)       { this.showToast('Agrega al menos un ingrediente', 'error'); return; }
      for (const ing of f.ingredientes) {
        if (!ing.id_materia_prima)      { this.showToast('Selecciona materia prima en todos los ingredientes', 'error'); return; }
        if (!ing.cantidad || ing.cantidad <= 0)
                                        { this.showToast('Cantidad inválida en un ingrediente', 'error'); return; }
      }

      this.savingReceta = true;
      try {
        const payload = {
          nombre:                f.nombre.trim(),
          id_producto_terminado: Number(f.id_producto_terminado),
          unidades_por_corrida:  Number(f.unidades_por_corrida),
          ingredientes:          f.ingredientes.map(i => ({
            id_materia_prima: Number(i.id_materia_prima),
            cantidad:         Number(i.paquetes || 1) * Number(i.cantidad),
          })),
        };

        if (this.modalReceta.editando) {
          await apiActualizarReceta(this.editandoRecetaId, this.idUsuario, payload);
          this.showToast('Receta actualizada', 'success');
        } else {
          await apiCrearReceta(this.idUsuario, payload);
          this.showToast('Receta creada', 'success');
        }
        this.cerrarModalReceta();
        await this.cargarRecetas();
      } catch (e) {
        this.showToast('Error al guardar: ' + (e.message || ''), 'error');
      } finally {
        this.savingReceta = false;
      }
    },

    async cambiarEstadoReceta(receta) {
      try {
        await apiToggleReceta(receta.id_receta, this.idUsuario, !receta.activo);
        receta.activo = !receta.activo;
        this.showToast(`Receta ${receta.activo ? 'activada' : 'desactivada'}`, 'success');
      } catch {
        this.showToast('Error al cambiar estado', 'error');
      }
    },

    // ─── Modal Nueva Orden ──────────────────────────────────────────────────

    abrirModalOrden() {
      this.ordenForm    = { id_receta: '', cantidad_a_producir: 1, margen_ganancia: 40, notas: '' };
      this.ordenPreview = null;
      this.ordenCreada = false;
      this.modalOrden.visible = true;
    },

    cerrarModalOrden() {
      this.modalOrden.visible = false;
      if (this.ordenCreada) {
        this.cargarOrdenes();
        this.ordenCreada = false;
      }
      this.ordenPreview = null;
    },

    async crearOrdenSubmit() {
      if (!this.ordenForm.id_receta)                                { this.showToast('Selecciona una receta', 'error'); return; }
      if (!this.ordenForm.cantidad_a_producir || this.ordenForm.cantidad_a_producir < 1)
                                                                    { this.showToast('Cantidad inválida', 'error'); return; }
      this.savingOrden = true;
      try {
        const result = await apiCrearOrden(this.idUsuario, {
          id_receta:           Number(this.ordenForm.id_receta),
          cantidad_a_producir: Number(this.ordenForm.cantidad_a_producir),
          margen_ganancia:     Number(this.ordenForm.margen_ganancia || 40),
          notas:               this.ordenForm.notas || null,
        });
        this.ordenPreview = result;
        this.ordenCreada = true;
        this.showToast('Orden creada en borrador', 'success');
      } catch (e) {
        this.showToast('Error: ' + (e.message || 'No se pudo crear la orden'), 'error');
      } finally {
        this.savingOrden = false;
      }
    },

    // ─── Modal Detalle ──────────────────────────────────────────────────────

    async verDetalleOrden(id_orden) {
      this.modalDetalle = { visible: true, data: null };
      this.isLoading = true;
      try {
        this.modalDetalle.data = await getOrdenDetalle(id_orden);
      } catch {
        this.showToast('Error al cargar detalle', 'error');
        this.modalDetalle.visible = false;
      } finally {
        this.isLoading = false;
      }
    },

    cerrarDetalle() { this.modalDetalle.visible = false; },

    // ─── Ejecutar / Cancelar ────────────────────────────────────────────────

    confirmarEliminarReceta(receta) { this.modalEliminarReceta = { visible: true, receta }; },

    async doEliminarReceta() {
      this.eliminandoReceta = true;
      try {
        await apiEliminarReceta(this.modalEliminarReceta.receta.id_receta, this.idUsuario);
        this.showToast('Receta eliminada', 'success');
        this.modalEliminarReceta.visible = false;
        await this.cargarRecetas();
      } catch (e) {
        this.showToast(e.message || 'Error al eliminar la receta', 'error');
      } finally {
        this.eliminandoReceta = false;
      }
    },

    confirmarEjecutar(orden) { this.modalEjecutar = { visible: true, orden }; },

    async doEjecutarOrden() {
      this.ejecutando = true;
      try {
        await apiEjecutarOrden(this.modalEjecutar.orden.id_orden, this.idUsuario);
        this.showToast('Orden ejecutada correctamente', 'success');
        this.modalEjecutar.visible = false;
        await this.cargarOrdenes();
      } catch (e) {
        this.showToast(e.message || 'Error al ejecutar la orden', 'error');
      } finally {
        this.ejecutando = false;
      }
    },

    confirmarCancelar(orden) { this.modalCancelar = { visible: true, orden }; },

    async doCancelarOrden() {
      this.cancelando = true;
      try {
        await apiCancelarOrden(this.modalCancelar.orden.id_orden, this.idUsuario);
        this.showToast('Orden cancelada', 'success');
        this.modalCancelar.visible = false;
        await this.cargarOrdenes();
      } catch (e) {
        this.showToast(e.message || 'Error al cancelar', 'error');
      } finally {
        this.cancelando = false;
      }
    },

    // ─── Helpers ────────────────────────────────────────────────────────────

    fmtNum(n) {
      if (n == null) return '0.00';
      return Number(n).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtFecha(dt) {
      if (!dt) return '—';
      return new Date(dt).toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    badgeOrden(estado) {
      return { borrador: 'badge-warn', completada: 'badge-active', cancelada: 'badge-off' }[estado] || 'badge-warn';
    },
    fmtMargen(o) {
      const precio = Number(o.receta?.producto?.precio_unitario || 0);
      const costo  = Number(o.costo_por_unidad || 0);
      if (!precio) return '—';
      const margen = ((precio - costo) / precio) * 100;
      return margen.toFixed(1) + '%';
    },
    margenClass(o) {
      const precio = Number(o.receta?.producto?.precio_unitario || 0);
      const costo  = Number(o.costo_por_unidad || 0);
      if (!precio) return '';
      const margen = ((precio - costo) / precio) * 100;
      if (margen >= 40) return 'margen-ok';
      if (margen >= 15) return 'margen-warn';
      return 'margen-bad';
    },
    showToast(msg, type = 'success') {
      this.toast = { visible: true, msg, type };
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.toast.visible = false; }, 3500);
    },
  },
};
</script>

<style scoped>
/* ── Variables ──────────────────────────────────────────────────────────────── */
.prod-page {
  --bg:       #f4f6f9;
  --surface:  #ffffff;
  --border:   #e2e8f0;
  --text:     #1e293b;
  --muted:    #64748b;
  --accent:   #16a34a;
  --accent-h: #15803d;
  --danger:   #dc2626;
  --warn:     #d97706;
  --row-alt:  #f8fafc;
  --shadow:   0 1px 3px rgba(0,0,0,.08);

  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
  padding: 1.5rem;
  font-family: inherit;
}

/* Dark mode */
.prod-page.dark {
  --bg:       #141414;
  --surface:  #1e1e1e;
  --border:   #3b3b3b;
  --text:     #e5e5e5;
  --muted:    #9ca3af;
  --accent:   #22c55e;
  --accent-h: #16a34a;
  --danger:   #f87171;
  --warn:     #fbbf24;
  --row-alt:  #252525;
  --shadow:   0 1px 4px rgba(0,0,0,.5);
  color-scheme: dark;
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.prod-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: .75rem;
}
.prod-header h2 { margin: 0; font-size: 1.4rem; font-weight: 700; display: flex; align-items: center; gap: .45rem; }
.prod-subtitle  { margin: .15rem 0 0; font-size: .85rem; color: var(--muted); }
.prod-header-btns { display: flex; gap: .5rem; align-items: center; }

/* ── Tabs ────────────────────────────────────────────────────────────────────── */
.prod-tabs {
  display: flex;
  gap: .25rem;
  border-bottom: 2px solid var(--border);
  margin-bottom: 1.25rem;
}
.prod-tab {
  background: none;
  border: none;
  padding: .55rem 1.1rem;
  font-size: .9rem;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color .15s, border-color .15s;
  display: flex;
  align-items: center;
  gap: .4rem;
}
.prod-tab:hover { color: var(--text); }
.prod-tab.active { color: var(--accent); border-bottom-color: var(--accent); }

/* ── Tab body ────────────────────────────────────────────────────────────────── */
.prod-tab-body { background: var(--surface); border-radius: .5rem; box-shadow: var(--shadow); overflow: hidden; }

/* ── Filters ─────────────────────────────────────────────────────────────────── */
.prod-filters {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--border);
}
.filter-item { display: flex; flex-direction: column; gap: .25rem; }
.filter-item label { font-size: .75rem; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; }

/* ── Table ───────────────────────────────────────────────────────────────────── */
.table-wrap { overflow-x: auto; }
.prod-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .875rem;
}
.prod-table thead tr { background: var(--row-alt); border-bottom: 2px solid var(--border); }
.prod-table th {
  padding: .7rem 1rem;
  text-align: center;
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--muted);
  white-space: nowrap;
}
.prod-table td { padding: .65rem 1rem; border-bottom: 1px solid var(--border); vertical-align: middle; text-align: center; }
.prod-table tbody tr:hover { background: var(--row-alt); }
.prod-table tbody tr.row-inactive { opacity: .55; }
.tc { text-align: center; }
.tr { text-align: right; }
.mono { font-variant-numeric: tabular-nums; }
.cell-name { font-weight: 500; }

/* ── States ──────────────────────────────────────────────────────────────────── */
.prod-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--muted);
  text-align: center;
}
.prod-empty i { font-size: 2.5rem; margin-bottom: .75rem; }
.prod-empty p { margin: .25rem 0; }
.prod-empty.small { padding: 1.5rem; }
.prod-empty.small i { font-size: 1.5rem; }

/* ── Badges ──────────────────────────────────────────────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  padding: .2rem .55rem;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 600;
  white-space: nowrap;
}
.badge-active { background: #dcfce7; color: #166534; }
.badge-off    { background: #f1f5f9; color: #64748b; }
.badge-warn   { background: #fef3c7; color: #92400e; }
.prod-page.dark .badge-active { background: #14532d; color: #86efac; }
.prod-page.dark .badge-off    { background: #2d2d2d; color: #6b7280; }
.prod-page.dark .badge-warn   { background: #451a03; color: #fcd34d; }

/* ── Buttons ─────────────────────────────────────────────────────────────────── */
.btn-primary {
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: .4rem;
  padding: .5rem 1rem;
  font-size: .875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  transition: background .15s;
}
.btn-primary:hover:not(:disabled)  { background: var(--accent-h); }
.btn-primary:disabled               { opacity: .6; cursor: not-allowed; }

.btn-secondary {
  background: none;
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: .4rem;
  padding: .5rem 1rem;
  font-size: .875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  transition: background .15s, border-color .15s;
}
.btn-secondary:hover { background: var(--row-alt); }
.prod-page.dark .btn-secondary { border-color: #444; }
.prod-page.dark .btn-secondary:hover { background: #2d2d2d; border-color: #555; }

.btn-danger {
  background: var(--danger);
  color: #fff;
  border: none;
  border-radius: .4rem;
  padding: .5rem 1rem;
  font-size: .875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
}
.btn-danger:hover:not(:disabled) { background: #b91c1c; }
.btn-danger:disabled { opacity: .6; cursor: not-allowed; }

.btn-sm { padding: .3rem .7rem; font-size: .8rem; }

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--muted);
  padding: .3rem .35rem;
  border-radius: .3rem;
  font-size: 1rem;
  transition: color .15s, background .15s;
}
.btn-icon:hover { background: var(--row-alt); color: var(--text); }
.btn-icon-sm {
  background: none;
  border: 1px solid var(--border);
  border-radius: .375rem;
  padding: .4rem .5rem;
  cursor: pointer;
  color: var(--muted);
  font-size: .9rem;
  transition: background .15s;
}
.btn-icon-sm:hover { background: var(--row-alt); }

.icon-green { color: var(--accent) !important; }
.icon-red   { color: var(--danger) !important; }
.icon-amber { color: #f59e0b !important; }
.icon-teal  { color: #0e7490 !important; }
.icon-muted { color: var(--muted); }

.btn-gold {
  background: #c09d62;
  color: #fff;
  border: none;
  border-radius: .4rem;
  padding: .5rem 1rem;
  font-size: .875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  transition: background .15s;
}
.btn-gold:hover { background: #a38655; }
.btn-gold:disabled { opacity: .6; cursor: not-allowed; }

.table-wrap-outer { display: flex; flex-direction: column; }
.pagination-wrapper { display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-top: 1px solid #dee2e6; }
.pagination-info { color: #6c757d; font-size: 0.875rem; }
.pagination-container { display: flex; gap: 5px; }
.pagination-button { padding: 6px 14px; border: 1px solid #dee2e6; background: white; cursor: pointer; border-radius: 6px; font-size: 0.875rem; font-family: "Montserrat", sans-serif; transition: all 0.3s ease; color: #212529; }
.pagination-button:hover:not(:disabled) { background: #c09d62; color: white; border-color: #c09d62; }
.pagination-button:disabled { opacity: 0.5; cursor: not-allowed; }
.prod-page.dark .pagination-wrapper { border-color: #404040; }
.prod-page.dark .pagination-info { color: #9e9e9e; }
.prod-page.dark .pagination-button { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.prod-page.dark .pagination-button:hover:not(:disabled) { background: #c09d62; color: white; border-color: #c09d62; }
.text-muted { color: var(--muted); font-size: .85rem; }

.actions-cell { white-space: nowrap; }

/* ── Inputs ──────────────────────────────────────────────────────────────────── */
.prod-input, .prod-select {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: .375rem;
  color: var(--text);
  padding: .45rem .6rem;
  font-size: .875rem;
  width: 100%;
  box-sizing: border-box;
  transition: border-color .15s, background .15s;
}
.prod-input:focus, .prod-select:focus {
  outline: none;
  border-color: var(--accent);
}
.prod-input::placeholder { color: var(--muted); opacity: .7; }
.prod-textarea { resize: vertical; font-family: inherit; }
.prod-page.dark .prod-input,
.prod-page.dark .prod-select {
  background: #141414;
  border-color: #444;
}
.prod-page.dark .prod-input:focus,
.prod-page.dark .prod-select:focus {
  border-color: var(--accent);
  background: #1a1a1a;
}
.prod-page.dark option { background: #1e1e1e; color: #e5e5e5; }

/* ── Form layouts ────────────────────────────────────────────────────────────── */
.form-group  { display: flex; flex-direction: column; gap: .35rem; }
.form-group label { font-size: .8rem; font-weight: 600; color: var(--muted); }
.req { color: var(--danger); }

.form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; }
.span-2 { grid-column: span 2; }
.span-3 { grid-column: span 3; }

@media (max-width: 600px) {
  .form-grid-2, .form-grid-3 { grid-template-columns: 1fr; }
  .span-2, .span-3 { grid-column: span 1; }
}

.field-note {
  margin: .35rem 0 0;
  font-size: .78rem;
  color: var(--warn);
  display: flex;
  align-items: center;
  gap: .3rem;
}

/* ── Ingredientes ────────────────────────────────────────────────────────────── */
.ing-section { margin-top: 1.5rem; }
.ing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .75rem;
}
.ing-title { font-size: .9rem; font-weight: 700; display: flex; align-items: center; gap: .4rem; }
.ing-empty {
  text-align: center;
  padding: 1rem;
  border: 1px dashed var(--border);
  border-radius: .375rem;
  color: var(--muted);
  font-size: .85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
}
.ing-list { display: flex; flex-direction: column; gap: .5rem; }
.ing-row  { display: flex; align-items: center; gap: .5rem; }
.ing-num  { font-size: .75rem; color: var(--muted); width: 1.25rem; text-align: right; flex-shrink: 0; }
.ing-select { flex: 1; }
.ing-cant-wrap { display: flex; align-items: center; gap: .3rem; width: 16rem; flex-shrink: 0; }
.ing-paq   { width: 4rem; flex-shrink: 0; }
.ing-sep   { font-size: .8rem; color: var(--muted); flex-shrink: 0; }
.ing-cant  { flex: 1; min-width: 0; }
.ing-unidad { font-size: .75rem; color: var(--muted); white-space: nowrap; }
.ing-total  { font-size: .78rem; color: var(--accent); font-weight: 600; white-space: nowrap; flex-shrink: 0; font-variant-numeric: tabular-nums; }

.margen-ok   { color: #16a34a; }
.margen-warn { color: #d97706; }
.margen-bad  { color: #dc2626; }
.prod-page.dark .margen-ok   { color: #4ade80; }
.prod-page.dark .margen-warn { color: #fbbf24; }
.prod-page.dark .margen-bad  { color: #f87171; }

/* ── Modals ──────────────────────────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.modal-box {
  background: var(--surface);
  border-radius: .6rem;
  box-shadow: 0 8px 32px rgba(0,0,0,.25);
  position: relative;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.prod-page.dark .modal-box {
  box-shadow: 0 8px 40px rgba(0,0,0,.65);
  border: 1px solid #3b3b3b;
}
.modal-sm { width: 100%; max-width: 420px; }
.modal-md { width: 100%; max-width: 560px; }
.modal-xl { width: 100%; max-width: 780px; }

.modal-close-x {
  position: absolute;
  top: .75rem;
  right: .75rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--muted);
  font-size: 1rem;
  padding: .2rem .4rem;
  border-radius: .3rem;
  z-index: 1;
  transition: color .15s, background .15s;
}
.modal-close-x:hover { color: var(--text); background: var(--row-alt); }

.modal-header {
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border);
}
.modal-header h3 { margin: 0; font-size: 1.05rem; font-weight: 700; display: flex; align-items: center; gap: .4rem; }

.modal-body {
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
}

/* ── Order preview ───────────────────────────────────────────────────────────── */
.order-preview { display: flex; flex-direction: column; gap: 1rem; }

.preview-banner {
  background: #dcfce7;
  color: #166534;
  border-radius: .4rem;
  padding: .75rem 1rem;
  font-size: .9rem;
  display: flex;
  align-items: center;
  gap: .5rem;
}
.prod-page.dark .preview-banner { background: #14532d; color: #86efac; }

.preview-costs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .75rem;
}
.preview-cost-item {
  background: var(--row-alt);
  border: 1px solid var(--border);
  border-radius: .4rem;
  padding: .75rem;
  text-align: center;
}
.preview-cost-item span { display: block; font-size: .75rem; color: var(--muted); margin-bottom: .2rem; }
.preview-cost-item strong { font-size: 1rem; font-variant-numeric: tabular-nums; }
.preview-cost-item.highlight strong { color: var(--accent); }

.stock-check { border: 1px solid var(--border); border-radius: .4rem; padding: .75rem; }
.stock-check-title { font-size: .8rem; font-weight: 700; color: var(--muted); margin-bottom: .5rem; }
.stock-row { display: flex; justify-content: space-between; align-items: center; font-size: .82rem; padding: .25rem 0; border-bottom: 1px solid var(--border); }
.stock-row:last-child { border-bottom: none; }
.stock-nombre { font-weight: 500; }
.stock-vals { display: flex; align-items: center; gap: .35rem; font-variant-numeric: tabular-nums; }
.stock-vals.ok   { color: var(--accent); }
.stock-vals.warn { color: var(--danger); }

.stock-warn-note {
  margin: .5rem 0 0;
  padding: .5rem .75rem;
  background: #fef2f2;
  color: #991b1b;
  border-radius: .375rem;
  font-size: .8rem;
  display: flex;
  align-items: flex-start;
  gap: .4rem;
}
.prod-page.dark .stock-warn-note { background: #450a0a; color: #fca5a5; }

.preview-hint { font-size: .8rem; color: var(--muted); margin: 0; }

/* ── Precio venta info ───────────────────────────────────────────────────────── */
.precio-venta-info {
  display: flex;
  align-items: center;
  gap: .4rem;
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  border-radius: .375rem;
  padding: .55rem .85rem;
  font-size: .85rem;
}
.precio-venta-info strong { font-variant-numeric: tabular-nums; }
.prod-page.dark .precio-venta-info { background: #1a2a1a; color: #86efac; border-color: #166534; }

/* ── Orden detalle ───────────────────────────────────────────────────────────── */
.detalle-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .75rem 1rem;
  background: var(--row-alt);
  border: 1px solid var(--border);
  border-radius: .4rem;
  padding: 1rem;
  margin-bottom: 1rem;
}
.detalle-info-item { display: flex; flex-direction: column; gap: .15rem; }
.detalle-info-item span { font-size: .72rem; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--muted); }
.detalle-info-item strong { font-size: .9rem; }
.span-full { grid-column: 1 / -1; }

.detalle-section-title {
  font-size: .85rem;
  font-weight: 700;
  color: var(--muted);
  margin-bottom: .5rem;
  display: flex;
  align-items: center;
  gap: .4rem;
  text-transform: uppercase;
  letter-spacing: .04em;
}

/* ── Toast ───────────────────────────────────────────────────────────────────── */
.prod-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  padding: .75rem 1.1rem;
  border-radius: .45rem;
  font-size: .875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: .5rem;
  z-index: 2000;
  box-shadow: 0 4px 16px rgba(0,0,0,.18);
  animation: slideIn .2s ease;
}
.prod-toast--success { background: #166534; color: #fff; }
.prod-toast--error   { background: #991b1b; color: #fff; }

@keyframes slideIn { from { transform: translateY(1rem); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

/* ── Spinner ─────────────────────────────────────────────────────────────────── */
@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin .75s linear infinite; }
</style>
